import { useCallback, useEffect, useRef, useState } from "react";
import { useApp } from "../../../context/AppContext";
import { fmtD as usD, fmtDT } from "../../../lib/usDate";
import { fmtMD as usMD } from "../../../lib/usDate";
import { usToday } from "../../../lib/usDate";

import { T, pickL } from "../../../lib/i18n";
import { ink } from "../../../lib/ink";
import { isStageClosed } from "./stageStatus";

export const WS_COLOR = {
  pre_sign_concept: { c: "#7c3aed", bg: "#f3e8ff" }, pending_site_measure: { c: "#b45309", bg: "#fef3c7" }, pending_land_survey: { c: "#b45309", bg: "#fef3c7" },
  floor_plan_design: { c: "#1d4ed8", bg: "#dbeafe" }, on_hold: { c: "#4b5b75", bg: "#f1f5f9" }, planning_package: { c: "#0f766e", bg: "#ccfbf1" },
  planning_plan_check: { c: "#0369a1", bg: "#e0f2fe" }, waiting_planning_payment: { c: "#a16207", bg: "#fef9c3" }, structural_design: { c: "#be185d", bg: "#fce7f3" }, building_package: { c: "#4338ca", bg: "#e0e7ff" },
  building_plan_check: { c: "#6d28d9", bg: "#ede9fe" }, handed_off: { c: "#15803d", bg: "#dcfce7" },
  // 「设计完成 · 结项」用青绿（teal）而非移交的绿：两者都是终态，但结项不进工程部，
  // 颜色与结项横幅、列表页签保持一致，避免被误读成「已移交施工」。
  design_completed: { c: "#0f766e", bg: "#ccfbf1" },
};
export const DS_COLOR = {
  not_started: { c: "#5f6f8a", bg: "#f1f5f9" }, waiting_site_measure: { c: "#b45309", bg: "#fef3c7" }, preparing: { c: "#0369a1", bg: "#e0f2fe" }, requested: { c: "#0f766e", bg: "#ccfbf1" },
  working: { c: "#1d4ed8", bg: "#dbeafe" }, submitted: { c: "#6d28d9", bg: "#ede9fe" }, in_plan_check: { c: "#4338ca", bg: "#e0e7ff" }, correction: { c: "#c2410c", bg: "#ffedd5" },
  stuck: { c: "#b91c1c", bg: "#fee2e2" }, approved: { c: "#15803d", bg: "#dcfce7" }, done: { c: "#166534", bg: "#bbf7d0" }, exempt: { c: "#475569", bg: "#e2e8f0" },
};

/* ---------- 阶段状态显示三态（2026-10-23 用户需求） ----------
   用户原话：「阶段状态不根据内在的产物提交状态改变哦…阶段任务可能就只有
   未开始..进行中..已完成...不要因为内在产物的状态就改变了阶段的状态显示哦」。

   先把事实讲清楚（避免后人再往「产物驱动阶段」方向排查）：
   阶段状态在库里是**独立字段** `design_stages.status`，产物状态（disciplines[].status）
   从来没有回写过它 —— 全库检索不到任何「产物状态 → 阶段状态」的写入路径。
   矩阵格子里看到的「就绪 / 已提交 / 等客户」不是产物状态，而是阶段自己的
   12 个细粒度状态被原样显示了出来；它和同一格里那行产物进度（如 0/4）纯属并列
   信息，只是恰好撞词（产物也有 submitted），看久了就会以为阶段跟着产物跑。

   所以本次**只做显示投影**，不动库、不动门禁：
   · 库内 12 态原样保留，继续驱动流程判定（顺序门禁 _stage_order_lock、
     「完成该阶段」门禁 stage_products_gate、逾期扫描、REOPENABLE_STATUSES 回退集合，
     全部按原始 status 判断，一张都不能改成三态）；
   · 只有「给人看的那个状态词」收敛成三态。

   映射口径（**按流程位置算，不按 currentStage 指针算**，见下面的「为什么」）：
     · Completed / Approved / Not Applicable      → 已完成
     · 其余状态，只有落在「流程前沿」那一格才 → 进行中
     · 其余状态且排在流程前沿之后              → 未开始（流程还没走到，产物提前交也不算数）
     · 其余状态且排在流程前沿之前              → 未开始（历史遗留：状态没标完成但流程已走过）

   【为什么不能跟着 currentStage 走（2026-10-23 第二次修正，用户当场指出的问题）】
   第一版把「`currentStage` == 本阶段」直接当作「进行中」，结果 ADU-0159 整行是
   前面 7 个阶段全「未开始」、唯独 P+B 批+Permit 显示「进行中 · 当前」——
   用户原话：「我要的是流程进度不能跳..但是产物可以提前提交......但是提交后
   流程状态不会被影响..你现在给我改成了...直接跳成后面的在进行中了」。
   这不是显示层的小毛病，`currentStage` 这个指针**本身就会旁路**：
   后端 `stage_close_core` 允许关掉任意一个已轮到 / 已过站的阶段，City「一次通过」
   更是一次关两个；只要被关的那个不是队首，指针就顺着 order 往前挪，
   于是「前面还没做、后面已成当前」。开发库实测 107 个在推进的项目里，
   有 13 个的 `currentStage` 指在前面仍有未完成阶段的位置（ADU-0159 / ADU-0099 /
   ADU-0091 等），它们的共同特征是前面阶段 `Not Started`、指针落到后面的 `Ready`。

   所以「进行中」改成由**流程位置**决定，即「它是第一个还没收口的阶段」：
     · `Ready`（关上一阶段时由 stage_close_core 写进来的「轮到你了」）→ 进行中 ✓
     · `In Progress` / `Submitted` / `Awaiting *` / `Correction Required` … 同理 ✓
     · 排在它后面的阶段，哪怕状态被标成 `In Progress`（产物提前交触发的人工改状态、
       或历史脏数据），显示仍是「未开始」—— 流程没走到就不许跳。
     · 排在它前面的阶段状态没标完成（历史遗留的 `Ready`），显示「未开始」而不是
       「进行中」—— 它只是漏了收口，不是正在做。

   实测依据（开发库 102 个在推进的项目）：队首阶段里 79 个是 `Ready`、16 个
   `Not Started`，只有 6 个真正写了 `In Progress`；同时序列表里「非队首却处于活动态」
   的样本为 0 —— 说明按位置判定既覆盖了真实在做的阶段，也不会把历史脏数据当进行中。

   ⚠️ 判「完成 / 逾期 / 可回退 / 能否关阶段」时仍必须用原始 status，
   不要拿这里的三态去判，否则会把 Blocked / 需整改 当成普通进行中放过去。 */
export const STAGE_PHASE = Object.freeze({ not_started: "not_started", in_progress: "in_progress", completed: "completed" });
export const STAGE_PHASE_LABEL = { not_started: "未开始", in_progress: "进行中", completed: "已完成" };
/* 配色沿用项目既有语义色：未开始=slate（中性）、进行中=sky（推进中）、已完成=emerald（收口）。 */
export const STAGE_PHASE_TONE = {
  not_started: { c: "#475569", bg: "#f1f5f9" },
  in_progress: { c: "#0369a1", bg: "#e0f2fe" },
  completed: { c: "#166534", bg: "#dcfce7" },
};
export const STAGE_PHASE_BADGE = {
  not_started: "bg-slate-100 text-slate-500",
  in_progress: "bg-sky-100 text-sky-700",
  completed: "bg-emerald-100 text-emerald-700",
};

/* ---------- 阶段合并（2026-10-23 用户需求） ----------
   结构 / T24 流程并入 Building 图。新项目在模板层就是一个阶段；存量项目仍有
   design_stages.structural_t24，因此前端必须按同一份映射把两行合成一个节点展示。

   这里只合并**展示与节点动作**，不删除历史阶段实例：
     · 矩阵：Building 图一格聚合两张底层阶段的状态、截止日与产物进度；
     · 详情 / 阶段会话：一个 Building 图节点下平铺两部分任务与产物；
     · 完成 / 回退：按节点内真实阶段逐条调用原接口，历史留档不丢。
   这与 P+B 的并发合并可叠加：concurrent 项目里 building_arch 仍属于 pb_set。 */
export const STAGE_CONSOLIDATIONS = Object.freeze([
  {
    key: "building_arch",
    name: "Building 图（含结构 / T24）",
    note: "含结构 / T24",
    keys: ["building_arch", "structural_t24"],
  },
]);

/** 某个底层阶段属于哪个固定合并节点；未合并的阶段返回 null。 */
export const stageConsolidationOf = (key) =>
  STAGE_CONSOLIDATIONS.find((g) => g.keys.includes(key)) || null;

/** 固定合并节点当前应展开的全部底层阶段 key。 */
export const stageMembersOfConsolidation = (key) =>
  (stageConsolidationOf(key)?.keys || [key]).slice();

/** 把历史 deep link（例如 structural_t24）归一到当前可见节点 key。 */
export const canonicalStageKey = (key) => stageConsolidationOf(key)?.key || key;

/**
 * 阶段原始状态是否已经是「有结论」（收口）。
 * 与后端 DONE_STATUSES / LEGACY_DONE 同口径：Completed / Approved / Not Applicable。
 * 只用于显示三态判定，不参与任何门禁。
 */
export const isStageClosedStatus = (status) => ["Completed", "Approved", "Not Applicable"].includes(String(status || ""));

/**
 * 阶段上是否已登记「City 已批准」（2026-11-04 客户反馈 1049 Steinway Ave）。
 *
 * 记录只证明 City 批了，**不代表本节点完成**：批准节点还含着 Permit 签发与第三笔款，
 * 所以它只影响显示三态与徒标文案，不参与 isStageClosed / 流程前沿 / 关阶段门禁。
 * 口径与后端 design_module.city_approval_of 一致：必须有批准日期才算登记。
 */
/* 【2026-11-04】正式登记优先，没有登记时退回后端按审图通过推导的结论。
   为什么要把推导并进来：后端只在展示层补 cityApprovalDerived（不写库），
   如果前端只读 cityApproval，矩阵 / 三态就看不到「已批准」，出现「详情已批准、矩阵未开始」。
   返回值带 derived 标记，调用方要区分正式记录与推导时用得上（登记按钮、徽标文案）。 */
export const cityApprovalOf = (stage) => (
  stage?.cityApproval?.approvedAt
    ? stage.cityApproval
    /* 推导结论不要求有批准日：审图阶段是按状态收口的存量项目往往没有 actualFinish，
       后端只能给出「按审图通过」而没有具体日期。此时仍要算「已批准」（页面写「日期未记录」），
       否则推导等于没做 —— 客户看到的还是 Not Started。 */
    : (stage?.cityApprovalDerived ? { ...stage.cityApprovalDerived, derived: true } : null)
);

/* City 结论节点（与后端 design_module.CITY_CONCLUSION_STAGES 一致）：
   审图 2 个 + 批准 2 个。外部审批按这 4 项等分，所以页面上要能说清「批了几项、还差哪项」。 */
export const CITY_CONCLUSION_STAGES = ["planning_review", "building_review", "planning_approval", "building_approval"];

/** 外部审批的两段口径：City 结论出了几项 + 百分比差的那一项是不是「等 Permit 签发」。
 *  纯展示，不参与任何门禁 / 付款判定；数字与后端 external_approval_progress 不冲突 ——
 *  后端按「结论节点是否收口」等分，这里把没收口的那一项解释成「结论已出、差 Permit」。
 */
export const cityApprovalProgressOf = (stages = []) => {
  const units = stages.filter((s) => s && CITY_CONCLUSION_STAGES.includes(s.key));
  if (!units.length) return null;
  const closed = (s) => ["Completed", "Approved", "Not Applicable"].includes(s.status) || !!s.actualFinish;
  const isApproval = (s) => s.key.endsWith("_approval");
  // 「结论已出」= 节点已收口，或批准节点已有（登记 / 推导的）City 批准结论。
  const concluded = (s) => closed(s) || (isApproval(s) && !!cityApprovalOf(s));
  const done = units.filter(concluded).length;
  // 剩余那一项是不是「City 已批、只差 Permit 签发与尾款」—— 用来解释百分比为什么没到 100%。
  const waitingPermit = units.filter((s) => !closed(s) && isApproval(s) && !!cityApprovalOf(s));
  const permitPending = waitingPermit.length > 0
    || units.some((s) => ((cityApprovalOf(s)?.pending) || []).includes("permit_not_issued"));
  return { done, total: units.length, permitPending, waitingPermit: waitingPermit.length };
};

/**
 * P+B 同步提交模式下的「外部审批」显示文字（2026-11-04 用户口径）。
 *
 * 为什么同步模式不再显示百分比：P+B 是一套图、City 一次性审、一次性批，
 * 而百分比仍按 4 个结论节点（P 审图 / B 审图 / P 批准 / B 批准）等分，
 * 于是「City 已经批了」永远只能显示 50%~75%，看起来像还差两块 —— 客户 1049 的
 * 误解正是从「外部审批 0%」开始的。同步模式下直接写状态文字，不再给容易误读的数字。
 * 分开提交（sequential）仍用百分比：那时 P、B 确实分两次批，等分是对的。
 *
 * 返回 null 表示「本项目没有 City 结论节点」（精简流程），调用方按原样显示。
 */
export const externalApprovalLabelOf = (stages = []) => {
  const cp = cityApprovalProgressOf(stages);
  if (!cp) return null;
  const approvals = stages.filter((s) => s && s.key && s.key.endsWith("_approval"));
  const closed = (s) => ["Completed", "Approved", "Not Applicable"].includes(s.status) || !!s.actualFinish;
  /* 批准节点全部收口 = City 批完、Permit 也签发了（批准节点关闭含 Permit 门禁）。 */
  if (approvals.length && approvals.every(closed)) return "City 已批准 · Permit 已签发";
  /* 已登记或按审图推导出 City 批准，但节点仍未收口 = 差 Permit 签发与尾款。 */
  if (cp.permitPending) return "City 已批准 · 待 Permit";
  if (cp.done > 0) return "City 审图已通过 · 待批准";
  return "City 审阅中";
};

/**
 * 一条流程（按显示顺序排列的节点）→ 每个节点 / 每个底层阶段的显示三态。
 *
 * 判定只有一条：**「进行中」永远给顺序里第一个还没收口的底层阶段**（流程前沿）。
 * 状态或历史实际完成时间均可表示收口，与后端项目进度及节点数量保持一致。
 *   · 它后面的阶段一律「未开始」—— 用户原话「流程进度不能跳..但是产物可以提前
 *     提交..但是提交后流程状态不会被影响」，所以排在前沿之后的阶段即使库里写成
 *     In Progress（历史脏数据 / 早期人工改状态）也不许显示成进行中；
 *   · 排在前沿之前却没收口的阶段同样「未开始」—— 那是历史漏标（关了后面忘了关
 *     前面），不是正在做；
 *   · 前沿阶段自己哪怕是 `Not Started`，也显示「进行中」—— 用户 2026-10-23 明确
 *     要求「第一个格子默认应该是进行中」：流程还没开工时，第一格就是下一步要做的
 *     事，标成未开始等于整行没有任何推进点。
 *   · 【2026-11-04】已登记 City 批准的节点也显示「进行中」：客户反馈「City 已批准，
 *     但系统节点一直显示 Not Started」。批准已取得是事实进度，不能因为前瞻节点还没
 *     收口（School Fee / Permit 未完成）就把它显示成没开工。仍然不把它算完成，
 *     所以流程前沿、关阶段门禁、付款与交接触发条件都不变。
 *
 * @param {{key: string, members: {key: string, status: string, actualFinish?: string}[]}[]} nodes
 *        显示顺序的节点数组。单阶段节点 members 传 [{key, status}]；
 *        P+B 合并节点传组内全部成员（顺序取组内书写顺序）。
 * @returns {{phaseOf: Object<string,string>, nodePhaseOf: Object<string,string>, frontierKey: string|null}}
 *          phaseOf 是底层阶段 key → 三态；nodePhaseOf 是节点 key → 三态
 *          （合并节点只要包含前沿就算进行中、全组收口才算已完成）；
 *          frontierKey 是流程当前所在的那个底层阶段（没有未收口阶段时为 null）。
 */
export const stagePhasesOf = (nodes = []) => {
  const phaseOf = {};
  const nodePhaseOf = {};
  let frontierKey = null;
  let frontierNode = null;
  for (const n of nodes) {
    const hit = (n.members || []).find((m) => !isStageClosed(m));
    if (hit) {
      frontierKey = hit.key;
      frontierNode = n && n.key;
      break;
    }
  }
  for (const n of nodes) {
    const ms = n.members || [];
    if (!ms.length) {
      nodePhaseOf[n.key] = STAGE_PHASE.not_started;
      continue;
    }
    const allClosed = ms.every(isStageClosed);
    // City 批准已登记但节点未收口：显示进行中（见上方 2026-11-04 注释）。
    const cityApproved = ms.some((m) => !isStageClosed(m) && !!cityApprovalOf(m));
    nodePhaseOf[n.key] = allClosed
      ? STAGE_PHASE.completed
      : (n.key === frontierNode || cityApproved ? STAGE_PHASE.in_progress : STAGE_PHASE.not_started);
    for (const m of ms) {
      phaseOf[m.key] = isStageClosed(m)
        ? STAGE_PHASE.completed
        : (m.key === frontierKey || !!cityApprovalOf(m) ? STAGE_PHASE.in_progress : STAGE_PHASE.not_started);
    }
  }
  return { phaseOf, nodePhaseOf, frontierKey };
};

/* P+B 同步提交模式的五个并发组（与后端 design_const.CONCURRENT_STAGE_GROUPS 逐项一致）。
   为什么前端也要有一份：流程前沿与顺序门禁必须按 P+B 固定五节点层序比较，
   否则旧库的 planning_paperwork(6) 会抢在 planning_review(8) 前面，把材料误判为
   审查前置。两边常量一旦分叉就会出现「前端按钮亮着、后端 400」或者反之。 */
export const CONCURRENT_GROUP_KEYS = Object.freeze([
  ["planning_set", "building_arch", "structural_t24"],
  ["planning_submit", "building_submit"],
  ["planning_review", "building_review"],
  ["planning_paperwork", "building_paperwork"],
  ["planning_approval", "building_approval"],
]);

/* P+B 固定有效层序：前后普通阶段 + 五个合并节点。
   这是**流程判定**顺序，不是 design_stages.order 的显示值；存量项目的旧
   order（材料在审查前）不会迁移，读取时统一映射到这份表。
   与后端 design_const.CONCURRENT_FLOW_UNITS 逐项一致。 */
export const CONCURRENT_FLOW_UNITS = Object.freeze([
  { key: "pre_consult", keys: ["pre_consult"] },
  { key: "contract", keys: ["contract"] },
  { key: "site_measure", keys: ["site_measure"] },
  { key: "schematic", keys: ["schematic"] },
  { key: "owner_signoff", keys: ["owner_signoff"] },
  { key: "pb_set", keys: CONCURRENT_GROUP_KEYS[0] },
  { key: "pb_submit", keys: CONCURRENT_GROUP_KEYS[1] },
  { key: "pb_review", keys: CONCURRENT_GROUP_KEYS[2] },
  { key: "pb_paperwork", keys: CONCURRENT_GROUP_KEYS[3] },
  { key: "pb_approval", keys: CONCURRENT_GROUP_KEYS[4] },
  { key: "permit_handoff", keys: ["permit_handoff"] },
]);

/**
 * P+B 流程门禁的有效收口状态。
 * 新模板已将 Structural / Title 24 并入 Building 图，但存量项目仍可能保留独立
 * structural_t24。Building 图完成后，旧阶段不再阻塞后续流程；只做判定投影，不改写
 * 原始状态与历史记录。该兼容规则仅适用于 concurrent 项目，前端与后端保持一致。
 */
export const isFlowStageClosed = (stage, stages = [], project = null) => {
  if (isStageClosedStatus(stage?.status) || stage?.actualFinish) return true;
  if ((project?.approvalMode || "sequential") !== "concurrent" || stage?.key !== "structural_t24") return false;
  const buildingStage = stages.find((row) => row?.key === "building_arch");
  return Boolean(buildingStage && (isStageClosedStatus(buildingStage.status) || buildingStage.actualFinish));
};

/** 底层阶段或 pb_xxx 合并节点 → P+B 固定层号；非 P+B 返回 null。 */
export const concurrentFlowOrderOf = (stageKey) => {
  const i = CONCURRENT_FLOW_UNITS.findIndex((u) => u.key === stageKey || u.keys.includes(stageKey));
  return i >= 0 ? i : null;
};

/* ---------- 有效流程层号（2026-10-24）：顺序门禁与显示同源 ----------
   【背景】2026-10-23 模板把 Building 材料挪到 Building 审之后，但存量项目的
   design_stages.order 仍是建项当天的旧顺序（building_paperwork=12 排在
   building_submit=13 之前）。详情页矩阵特意按当前模板显示（LegacyTabs
   orderedStageRows「不能直接相信 order」），顺序门禁（flowFrontierOf /
   flowOrderOf）却按库内 order 找前沿 —— 线上 3236 Rockhold 实测：屏幕上
   Building Submission 显示为当前步骤（蓝色 / 进行中），「完成该阶段」却被
   前沿=Building Paperwork 置灰，两边自相矛盾；线上 140 个存量项目全是旧 order。
   现在与后端 design_const.effective_flow_order 同日对齐：顺序项目也把阶段
   key 投影到「当前模板层号」再比较，门禁与矩阵永远同一份顺序。
   模板 key 缺省用 DEFAULT_FLOW_ORDER（镜像后端 ADU_TEMPLATE 的书写顺序）；
   调用方应优先传 meta.stageTemplate 的 key 列表（运行时权威），避免未来
   模板再调整时两处漂移。structural_t24 经 canonicalStageKey 并入
   building_arch 层，与后端 canonical_stage_key 同一套映射。 */
const DEFAULT_FLOW_ORDER = [
  "pre_consult", "contract", "site_measure", "schematic", "owner_signoff",
  "planning_set", "planning_paperwork", "planning_submit", "planning_review", "planning_approval",
  "building_arch", "building_submit", "building_review", "building_paperwork", "building_approval",
  "permit_handoff",
];

/** 模板 key 列表 → canonical key 层号表；同名 canonical 取首次出现的层号。 */
const flowRankMapOf = (templateKeys) => {
  const keys = Array.isArray(templateKeys) && templateKeys.length ? templateKeys : DEFAULT_FLOW_ORDER;
  const rank = {};
  keys.forEach((k, i) => {
    const c = canonicalStageKey(k);
    if (rank[c] == null) rank[c] = i;
  });
  return rank;
};

/** 阶段文档 → 门禁比较用有效层号：模板内取模板层号，模板外回退库内 order（与后端同口径）。 */
const effectiveFlowOrderOf = (s, rank) => {
  const r = rank[canonicalStageKey(s.key)];
  return r != null ? r : (s.order || 0);
};

/** 并发项目取固定层号；顺序项目按当前模板层号取，模板外回退传入的库内 order。 */
export const flowOrderOf = (stageKey, fallbackOrder, project = null, templateKeys = null) => {
  if ((project?.approvalMode || "sequential") === "concurrent") {
    const effective = concurrentFlowOrderOf(stageKey);
    if (effective != null) return effective;
  }
  /* 【2026-10-24 与显示同源】顺序项目按当前模板层号取，不再直接信库内 order：
     存量项目旧 order 与新模板顺序不一致（Building 材料在提交前），会让门禁锁住
     屏幕上显示为当前步骤的阶段（线上 3236 Rockhold 实测）。 */
  const r = flowRankMapOf(templateKeys)[canonicalStageKey(stageKey)];
  if (r != null) return r;
  return fallbackOrder;
};

/**
 * 流程前沿（flow frontier）：顺序门禁的唯一基准。与后端 design_const.flow_frontier 同口径。
 *
 * 【为什么门禁不能拿 currentStage 指针当基准（2026-10-23，用户「不能跳流程让阶段开始」）】
 * 上一版前后端都用 `project.currentStage` 的 order 比大小。问题是**指针自己会跳站**：
 *   · City「一次通过」要连关 planning_review + building_review，关掉第一个之后
 *     stage_close_core 就把指针推走了，关第二个时它已经落在指针之前；
 *   · 补关历史阶段、回退后重新推进，也会让指针跑在实际进度前面。
 * 开发库实测：107 个在途项目里 11 个指针在前沿之后（planning_review 仍是 Ready、
 * 指针已到 building_review）。这些项目「前面没做、后面却能点完成」= 跳流程。
 *
 * 新口径：顺序项目按**当前模板层号**升序找第一个还没收口的阶段（2026-10-24 起，
 * 与后端 design_const.effective_flow_order 同日对齐 —— 存量项目库内 order 是建项
 * 当天的旧顺序，2026-10-23 模板把 Building 材料挪到审查后之后，继续按它找前沿会把
 * 屏幕上显示为当前步骤的阶段锁死）；P+B 项目按 CONCURRENT_FLOW_UNITS 的五节点
 * 固定层序找第一个还没收口的节点。
 *   · 它自己永远放行 —— 「当前阶段必须能操作」由这条天然满足；
 *   · 排在它之后的阶段一律锁住，指针在哪都不影响。
 *
 * @param {{key:string, order:number, status:string, actualFinish?:string}[]} stages 原始阶段链（d.stages）
 * @param {{approvalMode?:string}} project 项目文档（并发组归并需要）
 * @param {string[]} [templateKeys] 当前模板的 key 顺序（meta.stageTemplate），缺省用内置镜像
 * @returns {{key:string, name:string, order:number}|null} order 是有效流程层号
 */
export const flowFrontierOf = (stages = [], project = null, templateKeys = null) => {
  const rank = flowRankMapOf(templateKeys);
  const rows = [...(stages || [])]
    .filter((s) => s && s.key)
    .sort((a, b) => {
      // 与后端 effective_flow_sort_key 同一全序：模板层号优先，模板外回退库内 order
      // 并整体排在模板阶段之后，两个量纲不混排。
      const ka = effectiveFlowOrderOf(a, rank);
      const kb = effectiveFlowOrderOf(b, rank);
      const ta = rank[canonicalStageKey(a.key)] != null ? 0 : 1;
      const tb = rank[canonicalStageKey(b.key)] != null ? 0 : 1;
      return ta - tb || ka - kb || (a.order || 0) - (b.order || 0);
    });
  if (!rows.length) return null;
  const concurrent = (project?.approvalMode || "sequential") === "concurrent";
  if (concurrent) {
    // P+B 必须按五节点固定层序找前沿；旧库的 planning_paperwork(order 6)
    // 不能越过 planning_review(8) 先成为当前节点。
    for (let order = 0; order < CONCURRENT_FLOW_UNITS.length; order += 1) {
      const unit = CONCURRENT_FLOW_UNITS[order];
      if (unit.keys.every((k) => PROJECT_HIDDEN_STAGE_KEYS.includes(k))) continue;
      const unitRows = rows.filter((s) => unit.keys.includes(s.key));
      if (!unitRows.length) continue;
      const open = unitRows.find((s) => !isFlowStageClosed(s, rows, project));
      if (open) return { key: open.key, name: open.name || open.key, order };
    }
    return null;
  }
  for (const s of rows) {
    if (PROJECT_HIDDEN_STAGE_KEYS.includes(s.key)) continue;
    if (isStageClosedStatus(s.status) || s.actualFinish) continue;
    // order 与 flowOrderOf 的返回同量纲（模板层号），stageTooEarlyOf 才能直接比。
    return { key: s.key, name: s.name || s.key, order: effectiveFlowOrderOf(s, rank) };
  }
  return null;
};

/**
 * 某个阶段（或合并节点）现在是否「还没轮到」—— 前端置灰按钮用。
 *
 * 与后端 design_v3._stage_order_lock 完全同一份判定：本阶段有效层号大于流程前沿
 * 层号就是「流程还没走到」。调用方对 P+B 节点需先走 flowOrderOf；顺序项目同样
 * 走 flowOrderOf（2026-10-24 起它返回模板层号），两侧量纲一致才不会误锁。
 *
 * @param {number|null} stageOrder 本阶段（或合并节点）用于比较的 order
 * @param {object|null} frontier flowFrontierOf 的结果
 * @returns {boolean} true = 流程还没走到，按钮该置灰
 */
export const stageTooEarlyOf = (stageOrder, frontier) => {
  if (stageOrder == null || !frontier || frontier.order == null) return false;
  return stageOrder > frontier.order;
};
/* 【2026-11-04 删除人工办理类状态文案覆盖表】
   原来这里把 working 显示成「办理中」、submitted 显示成「已递交」，理由是「跑机构的动作不是制作图纸」。
   实际后果是同一个状态键有两套中文名（产物卡与筛选器、自动化扫描、英文界面各说各的，
   而且这 5 条一直没有英文词条，切英文界面会露出中文）。
   用户口径：同一个状态键只留一个名字，显示名统一由服务端 discStatusMeta 下发。
   人工办理类「这是外部机构在办」的语义改由卡片上的机构名 / 凭证表达，不再靠改状态名暗示。 */
export const GROUP_COLOR = { "签约前": "#7c3aed", "前期": "#b45309", "规划": "#0f766e", "建筑": "#4338ca", "完成": "#15803d" };
export const fmtT = (s) => (s ? fmtDT(s) : "—");
export const fmtD = (s) => (s ? usD(s) : "—");
/* 短日期 MM/DD（矩阵格、阶段条、甘特用）。与 fmtD 同一套洛杉矶口径，
   避免页面里再用 `String(v).slice(5,10)` 直接截 UTC 字符串导致差一天。 */
export const fmtMD = (s) => (s ? usMD(s) : "—");
/* 有效评论（撤回的只留「已撤回」占位，不计入任何数量） */
export const liveComments = (p) => (p?.comments || []).filter((c) => !c.deleted);

/* ---------- 设计部各阶段 / 交付物上传的允许类型（2026-10-20 用户需求） ----------
   需求原话：「增加 ppt 的上传……最好兼容一下这些各个阶段的上传……都能传多种类型……比如 crd 这些」。
   具体白名单与取舍说明放在 lib/uploadAccept.js（components/ 也要引用，不能放页面目录），
   这里只是转出，页面侧沿用既有 `from "./meta"` 的导入方式。 */
export { DESIGN_UPLOAD_ACCEPT, DESIGN_PREVIEW_EXT, DESIGN_UPLOAD_EXT } from "../../../lib/uploadAccept";

/* ---------- 阶段任务必需文件类型（详情任务卡 / 阶段会话右侧 / 文件版本弹窗共用） ----------
   2026-10-19 用户反馈：阶段会话右侧和上传「类型」下拉直接显示英文键（如 811 Ticket），
   看不懂这个 811 到底是什么。这里把文件类型统一成中文名，并给业务上不直观的类型
   补一句解释；英文 / 西语模式仍由 TE / T 走原词典，不写死中文。 */
export const FILE_TYPE_ZH = { "Signed Approval": "签字确认书", "Signed Contract": "签字合同", "Fee Receipt": "缴费收据", "Planning PDF": "规划图 PDF", "Planning CAD": "规划图 CAD", "Building PDF": "建筑图 PDF", "P+B PDF": "P+B 图纸 PDF", "P+B CAD": "P+B 图纸 CAD", "Approved Plans": "批准图纸", Approval: "批准函", Permit: "施工许可证", "Job Card": "现场检查卡（Job Card）", Paperwork: "文书", Handoff: "移交包", Checklist: "核对清单", CAD: "CAD", Title24: "Title 24 能耗", Structural: "结构计算", "811 Ticket": "811 工单" };
/* 仅对「只看名字不知道是什么」的类型显示解释，避免每张卡都堆一长串说明。 */
export const FILE_TYPE_HINT = {
  "811 Ticket": "施工前向 811（地下管线定位服务）申请后取得的工单号 / 回执，用于确认开挖区域地下管线位置",
  "Job Card": "市府现场检查记录卡，记录检查结果与整改要求",
  Title24: "加州 Title 24 建筑能耗合规计算与报告",
  "Signed Approval": "业主签字确认的方案 / 变更确认书",
  "Signed Contract": "双方签字盖章的正式合同",
  "Fee Receipt": "向市府或机构缴费后取得的收据",
  Approval: "市府 / 机构出具的批准函",
  Permit: "市府签发的施工许可证",
  Handoff: "移交给施工方的完整资料包",
};

/* ---------- 旧版子任务 ↔ v3 产物 配对表（2026-10-21 用户需求） ----------
   背景：同一份交付在库里有两套记录 —— 旧版子任务（db.design_tasks，如
   「Site Plan + Floor Plan (CAD/PDF)」）和 v3 产物（disciplines，如 floor_plan）。
   一个阶段同时渲染两套卡就等于同一件事出现两遍，用户反馈「详情和类别矩阵聊天框
   右侧都重复了」。

   最终口径（用户逐条澄清后确定）：**保留任务卡，收起配对的产物卡**。
   用户原话：「应该保留上面那种…你保留反了」「两个边都是」「Planning Submission Set
   这个阶段也是保留上面的」「之前卡片本来就有这个功能啊 上传 → 送审 → 通过」。
   理由：任务卡本来就承载负责人 / 拖入即上传 / 文件与版本（逐版 Draft·Submitted·
   Approved）/ QA / 改期，是用户熟悉的主入口；产物卡只有「状态 + 文件数」。

   ⚠️ 这张表必须与后端 `design_v3.TASK_TO_DISC` / `design_module._PAIRED_TASK_TITLES`
   保持一致（改一处要三处一起改）：后端据此在产物终结时收口配对任务、并在门禁里
   认「同一交付的产物已审核通过」，前端据此决定哪张卡收起。
   放在 meta.js 而不是 LegacyTabs.jsx：DiscCfgDialog（全流程责任页签）也要用，
   从 LegacyTabs 引会把它对 Heavy 组件的依赖带进配置弹窗。 */
export const TASK_TO_DISC = {
  "Site Plan + Floor Plan (CAD/PDF)": "floor_plan",
  "Planning 全套 (PDF/CAD)": "planning",
  "Building Set (PDF/CAD)": "building",
  "Structural Calc/Plans": "structural",
  "Title 24 Report": "t_24",
};
/** 反向表：产物 key → 承载它的任务标题。 */
export const DISC_TO_TASK = Object.fromEntries(Object.entries(TASK_TO_DISC).map(([t, k]) => [k, t]));

/* ---------- 指派候选：只允许项目负责人（2026-10-19 用户需求） ----------
   需求：阶段 / 子任务里的「指派」下拉，实际含义是「谁去办理这件事」，
   候选不再罗列设计部全部同事，只允许从**项目负责人**里选：
   US、CN、交图员、矩阵协作负责人 / 完全独立模式的手选负责人。
   实现：统一由 projectOwnerOptions 生成候选，详情阶段卡与阶段会话右侧共用，
   避免两个入口的名单不一致。
   边界：任务历史负责人若已不在项目负责人列表里，仍保留一项并标「历史」，
   否则下拉会显示成「未指派」，看起来像负责人被清空了。 */
export const OWNER_SOURCE_LABEL = { owner: "US", cdDesigner: "CN", submissionOwner: "交图", collaborator: "协作", independent: "负责人", manager: "项目", legacy: "历史", designer: "设计部" };
export const projectOwnerOptions = (p, designers = [], current = "") => {
  const out = [];
  const seen = new Set();
  for (const m of p?.effectiveManagers || []) {
    const un = m?.username;
    // 未解析到系统账号的负责人不能作为 primaryOwner 存库，跳过。
    if (!un || m?.resolved === false || seen.has(un)) continue;
    seen.add(un);
    out.push({ username: un, name: m.name || un, source: m.source || "" });
  }
  if (!out.length) {
    // 项目还没配负责人（多为签约前项目）：这里不做「退回全部设计部同事」的兜底，
    // 否则又会变成任意同事都能被指派，违背用户「只能在负责人中选择」的要求。
    // 由调用方显示「请先在矩阵设置负责人」的提示，等负责人配好后再指派。
    return out;
  }
  if (current && !seen.has(current)) {
    const u = (designers || []).find((x) => x.username === current);
    out.push({ username: current, name: u?.name || current, source: "legacy" });
  }
  return out;
};

/** 负责人下拉选项文案：来源标签 + 姓名（US / CN / 交图 / 协作 / 历史…）。
 *  英文 / 西语模式下 source 由 T 翻译，姓名保持原样。 */
export const ownerOptionLabel = (u) => `${OWNER_SOURCE_LABEL[u.source] ? `${T(OWNER_SOURCE_LABEL[u.source])} · ` : ""}${u.name || u.username}`;

/* ---------- 项目管理不展示的签约前阶段（2026-10-19 用户需求） ----------
   需求：设计部「项目管理」列表（矩阵阶段列）与项目详情（阶段页签）不再显示
   「前期方案咨询」「合同签署」；「签约前营销方案」菜单仍要完整展示这两步。
   实现：只做展示层过滤，不改数据库、阶段实例或接口返回 —— 阶段任务、门禁、
   付款与签约联动仍按原有数据运行，避免为了隐藏两列而影响已有业务规则。
   判断依据是项目 phase：签约前（pre_sign）保留，进入签约后（signed / handed_off）隐藏。 */
export const PROJECT_HIDDEN_STAGE_KEYS = ["pre_consult", "contract"];
export const isStageHiddenInProjects = (key, phase) => phase !== "pre_sign" && PROJECT_HIDDEN_STAGE_KEYS.includes(key);

/* ---------- 项目终态判定（2026-10-20 用户需求） ----------
   需求原话：「有的项目不需要移交施工，设计完了就直接结束」。

   背景：系统原先只有 handed_off（已移交施工）一个终态，而 Design Only（只买图纸）
   在后端被明确禁止移交（客户自己报建、自己找施工队）。这类项目图纸做完后**没有出口**，
   永远留在「进行中」，逾期 / 停滞提醒会一直催。为此新增 design_completed（设计完成 · 结项）：
   同样是只读终态、进度 100%，区别只是不进工程部、不建工程项目。

   前端所有「是否已结束 / 是否只读」判断统一走这里，不要各页面自己写
   `phase === "handed_off"` —— 否则新增一个终态就要改十几处，漏一处就会出现
   「列表里已经结束、详情页还能编辑」的不一致。 */
export const CLOSED_STATUSES = ["handed_off", "design_completed"];
export const isClosedProject = (p) => !!p && (CLOSED_STATUSES.includes(p.phase) || CLOSED_STATUSES.includes(p.workStatus));
/** 是否为「设计完成 · 结项」（不进工程部）。详情页据此显示结项横幅 / 撤销结项入口。 */
export const isDesignCompleted = (p) => !!p && (p.phase === "design_completed" || p.workStatus === "design_completed");
/** 是否为「已移交施工」。工程项目链接 / 移交回执等 UI 只对这个状态出现。 */
export const isHandedOff = (p) => !!p && (p.phase === "handed_off" || p.workStatus === "handed_off");

/* ---------- 机构对接常量（阶段页签 / 矩阵快捷配置 / 机构对接页签共用） ----------
   ⚠️ DISC_AGENCY_MAP 只是**展示层**关联：design_agencies 没有 discKey 字段，
   产物与机构是两套独立存储。这张表用来把机构挂到对应产物卡片下，
   以及让矩阵格子点开时能直接找到"这个产物该找谁"。不写库、不参与任何门禁判定。 */
export const AGENCY_STATUSES = ["Not Started", "In Progress", "Submitted", "Approved", "Not Applicable"];
export const AG_ZH = { "Not Started": "未开始", "In Progress": "进行中", Submitted: "已提交", Approved: "已批准", "Not Applicable": "不适用" };
export const AG_DONE = ["Approved", "Not Applicable"];
export const APPLIC = ["Required", "Potentially Required", "Exempt/Not Applicable", "Unknown"];
export const APPLIC_ZH = { Required: "必需", "Potentially Required": "可能需要", "Exempt/Not Applicable": "不适用", Unknown: "待研判" };
export const AG_STATUS_BADGE = (s) => ["Approved", "Not Applicable"].includes(s) ? "bg-emerald-100 text-emerald-700"
  : ["Submitted"].includes(s) ? "bg-sky-100 text-sky-700"
    : ["In Progress"].includes(s) ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500";

export const DISC_AGENCY_MAP = {
  fire: ["Fire Sprinkler / Fire Dept"],
  grading: ["Grading Plan / Permit"],
  structural: ["Structural Engineering"],
  t_24: ["Title 24 / Energy"],
  building: ["Plumbing/Mechanical/Electrical"],
  will_serve_letter: ["Water Service / Will Serve"],
  fire_flow_test: ["Fire Flow Test"],
  school_fee: ["School Fee"],
  cnd: ["Construction & Demolition (C&D)"],
  sanitation: ["Sanitation / Sewer"],
  aqmd: ["AQMD / Air Quality"],
};
export const AGENCY_TO_DISC = Object.fromEntries(
  Object.entries(DISC_AGENCY_MAP).flatMap(([k, arr]) => arr.map((t) => [t, k])),
);
/** 某产物当前"该找的机构"（可能多个）；无对应产物时返回空数组 */
export const agenciesOfDisc = (agencies, discKey, map) => {
  const types = (map || DISC_AGENCY_MAP)[discKey] || [];
  return (agencies || []).filter((a) => types.includes(a.type));
};

/* ---------- 产物 ↔ 机构：可配置版（2026-10-18） ----------
   默认值就是上面的 DISC_AGENCY_MAP（按名称语义配的，属于「拿不准」的部分），
   主管可以在「产物时间设置 → 产物↔机构」里改，落库 db.settings._id="design_v3_disc_agency_map"。
   模块级缓存：一个会话只拉一次；阶段页签 / 矩阵快捷弹窗 / 配置弹窗共用同一份。
   ⚠️ 后端没上线或请求失败时静默回落到默认值 —— 这只是展示层关联，不值得为它报错。 */
let _daCache = null;
let _daPromise = null;
export const bustDiscAgencyCache = () => { _daCache = null; _daPromise = null; };
export const loadDiscAgencyMap = (api) => {
  if (_daCache) return Promise.resolve(_daCache);
  if (!_daPromise) {
    _daPromise = api.get("/design/v3/settings/disc-agency-map")
      .then(({ data }) => data?.map || DISC_AGENCY_MAP)
      .catch(() => DISC_AGENCY_MAP)
      .then((m) => { _daCache = m; return m; });
  }
  return _daPromise;
};
export const useDiscAgencyMap = (api) => {
  const [m, setM] = useState(_daCache || DISC_AGENCY_MAP);
  useEffect(() => {
    let live = true;
    loadDiscAgencyMap(api).then((x) => { if (live && x) setM(x); });
    return () => { live = false; };
  }, [api]);
  return m;
};
/** 机构类型 → 产物 key（反向查表，供「机构挂到哪个产物卡下面」用） */
export const reverseAgencyMap = (map) => Object.fromEntries(
  Object.entries(map || {}).flatMap(([k, arr]) => (arr || []).map((t) => [t, k])),
);

/* ---------- 卡片网格列数：按**容器实际宽度**算（2026-10-18） ----------
   抽屉可拖拽、可全屏，视口断点（sm/lg/xl）完全不准 —— 抽屉里 1440 屏也可能是 900px 宽。
   ⚠️ 必须 callback ref + ResizeObserver：数据是异步来的，首帧组件可能 return null，
   `useEffect([]) + ref` 那时 ref 是空的，观测器永远挂不上 → 列数卡死在默认值（「怎么又一行一个了」）。
   minCardW = 单卡最小宽度，maxCols = 上限；<200px 是抽屉展开动画中途，忽略。 */
export const useGridCols = (minCardW, maxCols = 4) => {
  const ro = useRef(null);
  const [cols, setCols] = useState(2);
  const bind = useCallback((el) => {
    ro.current?.disconnect?.();
    ro.current = null;
    if (!el) return;
    const gap = 8;
    const apply = (w) => {
      if (!w || w < 200) return;
      const raw = Math.floor((w + gap) / (minCardW + gap));
      // 容器窄到 470px 以下才允许单列；再窄也优先两列，避免「一行一个」把页面拉得很长
      setCols(Math.max(w >= 470 ? 2 : 1, Math.min(maxCols, raw)));
    };
    apply(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    ro.current = new ResizeObserver(([e]) => apply(e.contentRect.width));
    ro.current.observe(el);
  }, [minCardW, maxCols]);
  useEffect(() => () => ro.current?.disconnect?.(), []);
  return [cols, bind];
};


export const useV3Meta = () => {
  const { api } = useApp();
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    let live = true;
    const load = () => api.get("/design/v3/meta")
      .then(({ data }) => { if (live) setMeta(data); })
      // 失败时记下 HTTP 状态码：403 = 真的没有设计部权限，其它（网络 / 5xx）= 加载失败。
      // 原来两种情况都只置 error:true，页面只能显示「无权限…或加载失败」，管理员无法判断该去
      // 权限矩阵加权限，还是稍后重试 —— 正是这次「有页面权限仍显示无权限」难排查的原因之一。
      .catch((e) => { if (live) setMeta((old) => old || { error: true, status: e?.response?.status || 0 }); });
    load();
    // 产物类型 / 状态配置保存后，同一页面里的矩阵、详情和会话要立即换到新口径，
    // 不能要求用户刷新整个页面。
    window.addEventListener("dv3:meta-changed", load);
    return () => { live = false; window.removeEventListener("dv3:meta-changed", load); };
  }, [api]);
  return meta;
};

export const notifyV3MetaChanged = () => window.dispatchEvent(new Event("dv3:meta-changed"));

/** 当前产物可选状态；配置缺失时回退全局列表。 */
export const discStatusOptions = (meta, discKey, currentStatus) => {
  const configured = meta?.discStatusesByKey?.[discKey];
  const options = Array.isArray(configured) ? configured : (meta?.discStatuses || []);
  if (!currentStatus || options.includes(currentStatus)) return options;
  return [...options, currentStatus];
};

/** 状态显示名；人工办理项使用更贴合动作的文案。 */
export const discStatusLabel = (meta, status) =>
  /* discKind 形参保留（调用点很多，且未来可能再分叉），但不再影响显示名：
     2026-11-04 起设计产物与人工办理产物共用同一套状态文案。 */
  T(meta?.discStatusMeta?.[status]?.label || status);

/** 图纸版本排序（2026-10-20 用户需求 · 截图标注）。
 *
 * 需求原话：「这里需要做一个文件排序：最重要的是审批后的图纸，然后是第一次、
 * 第二次提交的图纸」。所以不再用单纯的版本号倒序，而是：
 *   ① 复核通过（review.status === "accepted"）的那一版置顶 —— 对外交付 / 报建以它为准；
 *      历史上有多个通过版本时，较新的那版更有效，审批组内按版本号倒序；
 *   ② 其余版本按版本号**正序**（v1 → v2 …），也就是第 1 次、第 2 次提交的先后；
 *   ③ 已作废版本沉底（仍展示，历史永久留档红线，由调用方用删除线弱化）。
 *
 * 不含版本号的记录（CRM 参考件 kind=crm_ref 记为 v0、nil）固定排在编好号的图纸之前：
 * 它们是「参考资料」而不是某一轮提交，混在 v1 / v2 之间会被误读成一次送审。
 *
 * 入参允许 `review` 或 `rv` 字段承载复核记录（详情接口用 review，历史数据两种都有）。
 * 返回新数组，不改动入参，避免 React 状态被就地排序污染。
 */
export const sortFileVersions = (files) => {
  const rvStatus = (f) => ((f && (f.review || f.rv)) || {}).status;
  // 无版本号 → 最前（参考件）；有版本号按数字比较。
  const verOf = (f) => {
    const v = f && f.version;
    return v == null || Number(v) <= 0 ? -1 : Number(v);
  };
  return [...(files || [])].sort((a, b) => {
    const av = !!a?.voided, bv = !!b?.voided;
    if (av !== bv) return av ? 1 : -1;
    const aa = rvStatus(a) === "accepted", ba = rvStatus(b) === "accepted";
    if (aa !== ba) return aa ? -1 : 1;
    return aa ? verOf(b) - verOf(a) : verOf(a) - verOf(b);
  });
};

/* ===== 阶段回退（2026-10-23）=====
 *
 * 需求背景：用户要「把已完成的阶段退回去重做」。回退时还有一个关键选择 ——
 * 下游那些**已完成**的阶段要不要跟着退。
 *
 * 踩过的坑（ADU-0059 实测反馈）：「我刚刚回退了本地的 7712，但是中间的阶段还是会显示已完成」。
 * 用户把「现场测量」退回时勾了「连带下游」，但当时的实现只会退**未完成**的下游阶段，
 * 紧跟在后面的「方案设计」（已完成）被留在原地，矩阵那一行就成了
 * 「进行中 → 已完成 → 未开始…」自相矛盾的样子；而 `blockedDownstream` 这个回报字段
 * 前端压根没展示，用户完全不知道发生了什么。
 *
 * 现在的口径：回退前先把下游的已完成阶段**列出来给人看**，再让人明确选
 * 「一起退回」还是「保留」。两个入口（详情阶段卡 / 矩阵阶段会话）必须用同一套筛选，
 * 否则同一件事从两个页面点进去会看到不同的名单。
 */

/** 后端认可的可回退状态（与 design_module.stage_reopen_core 的校验一致）。 */
export const REOPENABLE_STATUSES = ["Completed", "Approved", "Correction Required", "Reopened"];

/** 后端认定的「已完成」状态（与 design_module.DONE_STATUSES 一致）。 */
const DONE_ST = ["Approved", "Completed", "Not Applicable"];

/* 算出某个阶段（或合并节点）之后还有哪些阶段是「已完成」的 —— 回退确认弹窗用。
   合并节点传全部成员 key，取**最小有效层号**作基准：下游必须是「整组之后」才算下游，
   否则会把同组的兄弟阶段误当成下游列出来。
   【2026-10-24 与显示同源】下游改按「当前模板层号」算（不再用库内 order），与后端
   stage_reopen 的 cascade 下游集合同一口径：存量项目旧 order 里 building_paperwork(12)
   排在 building_submit(13) / building_review(14) 之前，按旧 order 找下游会漏掉屏幕上
   显示为本阶段下游的 paperwork，弹窗说「没有已完成的下游」、后端却把它一起退了。
   模板 key 优先传 meta.stageTemplate（运行时权威），缺省用内置镜像。 */
export const doneDownstreamOf = (stages, baseKeys, templateKeys = null) => {
  const rank = flowRankMapOf(templateKeys);
  const eff = (s) => {
    const r = rank[canonicalStageKey(s.key)];
    return r != null ? r : (s.order || 0);
  };
  const base = Math.min(...(baseKeys || []).map((k) => {
    const n = (stages || []).find((s) => s.key === k);
    return n ? eff(n) : Infinity;
  }).filter((x) => Number.isFinite(x)));
  if (!Number.isFinite(base)) return [];
  return (stages || [])
    .filter((s) => eff(s) > base)
    .filter((s) => DONE_ST.includes(s.status) || !!s.actualFinish)
    .sort((a, b) => eff(a) - eff(b))
    .map((s) => ({ key: s.key, name: s.name || s.key, status: s.status, actualFinish: s.actualFinish }));
};

export const WsPill = ({ ws, meta, size = "xs", tid, onClick }) => {
  const s = (meta?.workStatuses || []).find((x) => x.key === ws);
  const col = WS_COLOR[ws] || WS_COLOR.on_hold;
  // 2026-10-18 项目管理矩阵：状态名称必须单行展示，否则「待现场/土地测量」会把行高从 20px 撑到 36px，
  // 既破坏矩阵逐行比对，也减少表格可视行数。白名单只改共享状态胶囊的排版，不改变各页面的文案与颜色口径。
  const cls = `inline-flex items-center gap-1 whitespace-nowrap rounded-full font-semibold ${size === "xs" ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"}`;
  if (onClick) return <button type="button" data-testid={tid} onClick={onClick} title={T("点击只看此状态")} className={`${cls} hover:ring-2 hover:ring-offset-1 hover:ring-slate-300 transition-shadow`} style={ink(col.c, col.bg)}><i className="w-1.5 h-1.5 rounded-full" style={{ background: col.c }} />{pickL(s, "name", "sub") || ws}</button>;
  return <span data-testid={tid} className={cls} style={ink(col.c, col.bg)}><i className="w-1.5 h-1.5 rounded-full" style={{ background: col.c }} />{pickL(s, "name", "sub") || ws}</span>;
};

export const DsPill = ({ status, meta, tid, onClick, small, discKind }) => {
  const col = DS_COLOR[status] || DS_COLOR.not_started;
  const cls = `inline-flex items-center rounded-md font-semibold whitespace-nowrap ${small ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-1"}`;
  const label = discStatusLabel(meta, status, discKind);
  if (!onClick) return <span data-testid={tid} className={cls} style={ink(col.c, col.bg)}>{label}</span>;
  return <button type="button" data-testid={tid} onClick={onClick} className={`${cls} hover:ring-2 hover:ring-offset-1 hover:ring-slate-300 transition-shadow`} style={ink(col.c, col.bg)}>{label}</button>;
};

export const formatProgress = (v) => (v == null ? "—" : `${v}%`);

export const Progress = ({ v, color = "#0f172a", h = "h-1.5" }) => (
  <div className={`w-full ${h} rounded-full bg-slate-100 overflow-hidden`}><div className={`${h} rounded-full transition-all`} style={{ width: `${Math.min(100, v || 0)}%`, background: color }} /></div>
);

export const ringColor = (pct, th) => (pct >= (th?.green ?? 100) ? "#10b981" : pct >= (th?.blue ?? 60) ? "#3b82f6" : "#f59e0b");

export const Ring = ({ v, size = 44, stroke = 5, color, label, tid, className = "", th }) => {
  const pct = Math.max(0, Math.min(100, Math.round(v || 0)));
  const r = (size - stroke) / 2, c = 2 * Math.PI * r, col = v == null ? "#94a3b8" : color || ringColor(pct, th);
  return (
    <div className={`relative shrink-0 ${className}`} style={{ width: size, height: size }} data-testid={tid} title={v == null ? T("暂无阶段进度") : T("总进度 {0}%（按阶段权重及任务完成度计算）", [pct])}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90"><circle cx={size / 2} cy={size / 2} r={r} stroke="#e2e8f0" strokeWidth={stroke} fill="none" /><circle cx={size / 2} cy={size / 2} r={r} stroke={col} strokeWidth={stroke} fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} className="transition-[stroke-dashoffset] duration-700" /></svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none"><b style={{ color: col, fontSize: size >= 80 ? 18 : size >= 56 ? 13 : 10 }}>{v == null ? "—" : `${pct}%`}</b>{label && <span className="text-[10px] text-slate-500 mt-0.5">{label}</span>}</div>
    </div>
  );
};

export const StaleChip = ({ p, tid }) => (p.isStale ? <span data-testid={tid} title={T("最近更新 {0}", [fmtD(p.lastUpdatedAt)])} className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-800 whitespace-nowrap">{T("{0} 天无更新", [p.staleDays])}</span> : null);

export const stageForecastDaysLeft = (p) => {
  const target = String(p?.stageForecastDate || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(target)) return null;
  // 2026-10-29：与 usDate 显示层同源，避免「显示用美国日、算天数用另一套」两套口径。
  const today = usToday();
  const targetMs = Date.parse(`${target}T00:00:00Z`);
  const todayMs = Date.parse(`${today}T00:00:00Z`);
  return Number.isFinite(targetMs) && Number.isFinite(todayMs) ? Math.round((targetMs - todayMs) / 86400000) : null;
};

export const stageForecastState = (p) => {
  const n = stageForecastDaysLeft(p);
  return n === null ? null : n < 0 ? "overdue" : n <= 3 ? "due_soon" : "ok";
};

export const DeadlineBadge = ({ p, tid }) => {
  const forecastDay = String(p?.stageForecastDate || "").slice(0, 10);
  const n = stageForecastDaysLeft(p);
  if (!forecastDay || n === null) return null;
  const cls = n < 0 ? "bg-red-100 text-red-700" : n <= 3 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600";
  const txt = n < 0 ? T("逾期 {0} 天", [-n]) : n === 0 ? T("今日到期") : T("剩 {0} 天", [n]);
  const dateLabel = fmtD(forecastDay);
  const title = T("阶段预计完成日期 {0}", [dateLabel]);
  return <span data-testid={tid} title={title} className={`inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-bold px-1.5 py-0.5 rounded-md ${cls}`}>{txt} · {dateLabel}</span>;
};

/**
 * 「标准结束日」徽标（2026-10-27 新增，**纯展示字段**）。
 *
 * 需求来源（用户 2026-10-27 定调）：
 *   · 「82 这个工作日是给老板看的..阶段上不显示..阶段只按照阶段来计算是否延期..
 *      因为会有自然顺延的问题」；
 *   · 「这个 82 个工作日实际是用来给老板或者管理员看我规定的这个 82 个工作日每个项目
 *      逾期了多久来做判断的...和业务无关」；
 *   · 「只显示在列表增加一个预计结束日期的显示，其他不管阶段的限制」；
 *   · 「这个展示字段红就行了呀...后方阶段不显示对应的逾期天数就行了」。
 *
 * 所以这里只做一件事：把后端 `stdBaseline`（签约日 + 82 个美国工作日）算出的日期画出来，
 * 超期标红。**不做任何判定** —— 不参与阶段红框、不参与任何筛选，
 * 阶段格子的红框 / 超期天数仍然只由 `design_stages.dueDate` 决定。
 *
 * 与「节点」列（DeadlineBadge，读 stageForecastDate）的区别，不要合并：
 *   · 节点列 = 阶段排期末端，会随 City 退图 / 测绘 / 人工改期等自然顺延一起变，是业务时间；
 *   · 本列 = 合同口径的死数（签约日 + 82 天，永不随阶段顺延），只给管理层当尺子量「拖了多久」。
 * 两者并排显示正是需求：老板看合同尺子，设计和工程看阶段排期。
 *
 * 未超期时只显示日期、**不显示「剩 N 天」**：这一列不是业务截止日，做成倒计时会让人
 * 误以为项目真有这么个交付期限，进而拿它去卡阶段 —— 那正是本次要拆开的东西。
 * 超期文案用「超期」而不是「逾期」，就是为了和阶段侧「逾期 N 天」在措辞上分开。
 */
export const StdBaselineBadge = ({ p, tid }) => {
  const sb = p?.stdBaseline;
  const day = String(sb?.date || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
    // 没有签约日也没有建项日的脏数据：显示破折号，不猜、不兜默认值。
    return <span data-testid={tid} data-overdue="0" className="text-[10px] text-slate-300">—</span>;
  }
  const over = sb?.overdue === true;
  const n = Number(sb?.overdueDays) || 0;
  const dateLabel = fmtD(day);
  const txt = over ? T("超期 {0} 天 · {1}", [n, dateLabel]) : dateLabel;
  /* 【2026-10-29 补解释】IT 逾期报告里 37 Huntington（超期 810 天）和 306 Jackson
     （超期 303 天）被当成「当前阶段实际延误」来质疑。数字本身是对的（签约日 2024-04
     + 82 个美国工作日 ≈ 2024-08），问题在于**页面从来没说过这一列是合同尺子**：
       · 它是「签约日 + 82 美国工作日」的固定基线，永不随阶段顺延变化；
       · 它不代表当前阶段排期，也不代表设计部今天在做的事超期了。
     title 里补上这两句，并把签约日也写出来，让用户能自己把这个数算回去。

     文案刻意做成**两条完整句子**（而不是 3 段 T() 拼接）：i18n 的运行时翻译按整句
     命中词典，拼接出的半句会被 LLM 逐段重译，出现「Milestone」单数、空格被吞等瑕疵。
     超期与未超期各一条 key，未超期那条不带「已超期」尾巴，避免英文里出现空占位。 */
  const title = over
    ? T("标准结束日 {0}：签约日 {1} + {2} 个美国工作日（合同口径固定基线，不随阶段顺延变化） · 已超期 {3} 天。这是合同基准偏差，不等于当前阶段或设计部的延误；当前阶段排期请看「节点」列。", [
      dateLabel, fmtD(sb?.startDate) || "—", sb?.totalDays ?? "—", n,
    ])
    : T("标准结束日 {0}：签约日 {1} + {2} 个美国工作日（合同口径固定基线，不随阶段顺延变化）", [
      dateLabel, fmtD(sb?.startDate) || "—", sb?.totalDays ?? "—",
    ]);
  return (
    <span data-testid={tid} data-overdue={over ? 1 : 0} data-days={sb?.days ?? ""} title={title}
      className={`inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-bold px-1.5 py-0.5 rounded-md ${over ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>
      {txt}
    </span>
  );
};
